import Breadcrumb from "components/Common/Breadcrumb"
import React, { useState, useRef, useMemo, useEffect } from "react"
import {
  FaUpload,
  FaFileExcel,
  FaTimes,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa"
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Input,
} from "reactstrap"
import Swal from "sweetalert2"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import * as XLSX from "xlsx"

const POSSheetNames = () => {
  const [headers, setHeaders] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const fileInputRef = useRef(null)

  const toggleModal = () => {
    setModalOpen(prev => !prev)
    setDragOver(false)
  }

  useEffect(() => {
    getPOSNames()
  }, [])

  async function getPOSNames() {
    const APIData = await fetchData(setUploading, "pos")
    if (APIData?.success) {
      setHeaders(APIData?.data)
    }
  }

  const processFile = async file => {
    if (!file) return
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      alert("Please upload a valid Excel file (.xlsx or .xls)")
      return
    }

    const APIdata = await FetchPostData(setUploading, "pos", { pos: file })
    if (APIdata?.data?.success) {
      Swal.fire(
        "File Imported Successfully.",
        APIdata?.data?.message,
        "success"
      )
      getPOSNames()
      setModalOpen(false)
    } else {
      Swal.fire(
        "Error Importing File.",
        APIdata?.response?.data?.message,
        "error"
      )
    }
  }

  const handleFileInput = e => processFile(e.target.files[0])

  const handleDrop = e => {
    e.preventDefault()
    setDragOver(false)
    processFile(e.dataTransfer.files[0])
  }

  const handleDragOver = e => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleClear = () => {
    setHeaders([])
    setSearchQuery("")
    setPage(1)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  // Filter headers by search
  const filteredHeaders = useMemo(
    () =>
      headers.filter(h =>
        h?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [headers, searchQuery]
  )

  // Pagination
  const totalPages = Math.ceil(filteredHeaders.length / rowsPerPage)
  const paginatedHeaders = filteredHeaders.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  )

  return (
    <div className="page-content">
      <Breadcrumb title="Settings" breadcrumbItem="POS Sheet Names" />

      <Card className="shadow-sm border-0">
        <CardBody>
          {/* Header row */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h4 className="mb-0 fw-bold" style={{ color: "#1a1a2e" }}>
              POS Sheet Headers
            </h4>
            <div className="d-flex gap-3">
              <Button
                className="btn-blue d-flex align-items-center gap-2"
                onClick={toggleModal}
              >
                <FaUpload /> Upload Excel File
              </Button>
            </div>
          </div>

          {/* Empty state */}
          {headers.length === 0 && (
            <div
              className="d-flex flex-column align-items-center justify-content-center py-5 rounded"
              style={{
                border: "2px dashed #dee2e6",
                background: "#f8f9fa",
                minHeight: 220,
                cursor: "pointer",
              }}
              onClick={toggleModal}
            >
              <FaFileExcel
                size={48}
                style={{ color: "#b0bec5", marginBottom: 12 }}
              />
              <p className="text-muted fw-semibold mb-1">
                No file uploaded yet
              </p>
              <p className="text-muted small mb-3">
                Click or drag &amp; drop an Excel file to get started
              </p>
              <Button
                className="btn-blue"
                size="sm"
                onClick={e => {
                  e.stopPropagation()
                  toggleModal()
                }}
              >
                <FaUpload className="me-2" /> Choose File
              </Button>
            </div>
          )}

          {/* Headers table */}
          {headers.length > 0 && (
            <>
              {/* Search + rows per page */}
              <div className="d-flex justify-content-start align-items-center mb-2 flex-wrap gap-3">
                <Input
                  type="select"
                  style={{ maxWidth: 100 }}
                  value={rowsPerPage}
                  onChange={e => {
                    setRowsPerPage(Number(e.target.value))
                    setPage(1)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </Input>
                <Input
                  type="text"
                  className="form-control"
                  style={{ maxWidth: 320 }}
                  placeholder={`Search ${headers.length} columns…`}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>

              <div style={{ overflowX: "auto", borderRadius: 8 }}>
                <Table
                  bordered
                  responsive
                  striped
                  hover
                  className="mb-0"
                  style={{ fontSize: 14 }}
                >
                  <thead style={{ background: "#1a1a2e", color: "#fff" }}>
                    <tr>
                      <th
                        style={{
                          width: 52,
                          background: "#1a1a2e",
                          color: "#adb5bd",
                        }}
                      >
                        #
                      </th>
                      <th style={{ background: "#1a1a2e", color: "#fff" }}>
                        Column Header
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedHeaders.length > 0 ? (
                      paginatedHeaders.map((header, index) => {
                        return (
                          <tr key={index}>
                            <td
                              className="text-muted text-center"
                              style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {(page - 1) * rowsPerPage + index + 1}
                            </td>
                            <td className="fw-semibold">
                              {header?.headerName}
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={2} className="text-center text-muted py-4">
                          No columns match &ldquo;{searchQuery}&rdquo;
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination controls */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="align-self-center">
                  Page {page} of {totalPages || 1}
                </span>
                {/* ===== PAGINATION ===== */}
                <div className="d-flex justify-content-center align-items-center gap-2 ">
                  {/* Previous */}
                  <Button
                    disabled={page === 1}
                    onClick={() => setPage(prev => prev - 1)}
                    className="btn-blue"
                  >
                    <FaChevronLeft />
                  </Button>

                  {/* Page Numbers (Show 5 at a time) */}
                  {(() => {
                    const pageNumbers = []
                    const maxVisiblePages = 5

                    let startPage = Math.max(
                      1,
                      page - Math.floor(maxVisiblePages / 2)
                    )

                    let endPage = startPage + maxVisiblePages - 1

                    if (endPage > totalPages) {
                      endPage = totalPages
                      startPage = Math.max(1, endPage - maxVisiblePages + 1)
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <Button
                          key={i}
                          color={page === i ? "primary" : "light"}
                          onClick={() => setPage(i)}
                        >
                          {i}
                        </Button>
                      )
                    }

                    return pageNumbers
                  })()}

                  {/* Next */}
                  <Button
                    disabled={page === totalPages || totalPages === 0}
                    onClick={() => setPage(prev => prev + 1)}
                    className="btn-blue"
                  >
                    <FaChevronRight />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Upload Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader toggle={toggleModal}>
          <FaFileExcel className="me-2 text-success" />
          Upload Excel File
        </ModalHeader>

        <ModalBody>
          {/* Drag-and-drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            className="d-flex flex-column align-items-center justify-content-center rounded"
            style={{
              border: `2px dashed ${dragOver ? "#1976d2" : "#dee2e6"}`,
              background: dragOver ? "#e3f2fd" : "#f8f9fa",
              minHeight: 160,
              cursor: "pointer",
              transition: "all 0.2s ease",
              padding: 24,
            }}
          >
            <FaUpload
              size={32}
              style={{
                color: dragOver ? "#1976d2" : "#b0bec5",
                marginBottom: 10,
              }}
            />
            <p
              className="fw-semibold mb-1"
              style={{ color: dragOver ? "#1976d2" : "#495057" }}
            >
              {dragOver ? "Drop your file here" : "Drag & drop your Excel file"}
            </p>
            <p className="text-muted small mb-2">or click to browse</p>
            <span
              className="badge bg-light text-secondary border"
              style={{ fontSize: 11 }}
            >
              .xlsx &nbsp;·&nbsp; .xls
            </span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileInput}
            style={{ display: "none" }}
          />

          {uploading && (
            <div className="mt-3 d-flex align-items-center gap-2 text-primary">
              <div className="spinner-border spinner-border-sm" role="status" />
              <span>Processing file…</span>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" outline onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default POSSheetNames
